import { useState, useEffect, useCallback } from "react";
import {
  getContributionRequests,
  acceptContributionRequest,
  rejectContributionRequest,
  getContributions,
} from "@/lib/api/contributions";
import { getCached, setCached, isFresh } from "@/lib/api/cache";
import type {
  ContributionRequest,
  Contribution,
  ContributionRequestDirection,
  ContributionRequestStatus,
  ContributionStatus,
} from "@/types/contribution";
import type { PaginationMeta } from "@/types/api";

export interface UseContributionsReturn {
  requests: ContributionRequest[];
  requestsLoading: boolean;
  requestsMeta: PaginationMeta | null;
  contributions: Contribution[];
  contributionsLoading: boolean;
  contributionsMeta: PaginationMeta | null;
  error: string | null;
  actionLoading: boolean;
  // Request filters
  direction: ContributionRequestDirection;
  setDirection: (d: ContributionRequestDirection) => void;
  requestStatus: ContributionRequestStatus | undefined;
  setRequestStatus: (s: ContributionRequestStatus | undefined) => void;
  requestSearch: string;
  setRequestSearch: (s: string) => void;
  requestPage: number;
  setRequestPage: (p: number) => void;
  // Contribution filters
  contributionStatus: ContributionStatus | undefined;
  setContributionStatus: (s: ContributionStatus | undefined) => void;
  contributionSearch: string;
  setContributionSearch: (s: string) => void;
  contributionPage: number;
  setContributionPage: (p: number) => void;
  // Actions
  acceptRequest: (id: number) => Promise<boolean>;
  rejectRequest: (id: number) => Promise<boolean>;
  refetchRequests: () => Promise<void>;
  refetchContributions: () => Promise<void>;
}

export function useContributions(): UseContributionsReturn {
  // Requests state
  const [requests, setRequests] = useState<ContributionRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestsMeta, setRequestsMeta] = useState<PaginationMeta | null>(
    null
  );
  const [direction, setDirection] =
    useState<ContributionRequestDirection>("received");
  const [requestStatus, setRequestStatus] = useState<
    ContributionRequestStatus | undefined
  >();
  const [requestSearch, setRequestSearch] = useState("");
  const [requestPage, setRequestPage] = useState(1);

  // Contributions state
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [contributionsLoading, setContributionsLoading] = useState(true);
  const [contributionsMeta, setContributionsMeta] =
    useState<PaginationMeta | null>(null);
  const [contributionStatus, setContributionStatus] = useState<
    ContributionStatus | undefined
  >();
  const [contributionSearch, setContributionSearch] = useState("");
  const [contributionPage, setContributionPage] = useState(1);

  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchRequests = useCallback(async () => {
    const cacheKey = `contribution-requests:${direction}:${requestStatus ?? ''}:${requestSearch}:${requestPage}`;
    try {
      const cached = getCached<ContributionRequest[]>(cacheKey);
      if (cached) setRequests(cached);
      if (isFresh(cacheKey)) {
        setRequestsLoading(false);
        return;
      }
      setRequestsLoading(true);
      const response = await getContributionRequests({
        direction,
        status: requestStatus,
        search: requestSearch || undefined,
        page: requestPage,
      });
      if (response.success && response.data) {
        setCached(cacheKey, response.data, 60_000);
        setRequests(response.data);
        setRequestsMeta(response.meta ?? null);
      } else {
        if (!cached) {
          setRequests([]);
          setRequestsMeta(null);
        }
      }
    } catch {
      const cached = getCached<ContributionRequest[]>(cacheKey);
      if (!cached) setRequests([]);
    } finally {
      setRequestsLoading(false);
    }
  }, [direction, requestStatus, requestSearch, requestPage]);

  const fetchContributions = useCallback(async () => {
    const cacheKey = `contributions:${contributionStatus ?? ''}:${contributionSearch}:${contributionPage}`;
    try {
      const cached = getCached<Contribution[]>(cacheKey);
      if (cached) setContributions(cached);
      if (isFresh(cacheKey)) {
        setContributionsLoading(false);
        return;
      }
      setContributionsLoading(true);
      const response = await getContributions({
        status: contributionStatus,
        search: contributionSearch || undefined,
        page: contributionPage,
      });
      if (response.success && response.data) {
        setCached(cacheKey, response.data, 60_000);
        setContributions(response.data);
        setContributionsMeta(response.meta ?? null);
      } else {
        if (!cached) {
          setContributions([]);
          setContributionsMeta(null);
        }
      }
    } catch {
      const cached = getCached<Contribution[]>(cacheKey);
      if (!cached) setContributions([]);
    } finally {
      setContributionsLoading(false);
    }
  }, [contributionStatus, contributionSearch, contributionPage]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    fetchContributions();
  }, [fetchContributions]);

  const acceptRequest = useCallback(
    async (id: number): Promise<boolean> => {
      try {
        setActionLoading(true);
        const response = await acceptContributionRequest(id);
        if (response.success) {
          await fetchRequests();
          return true;
        }
        setError(response.message ?? "Failed to accept request");
        return false;
      } catch {
        setError("An unexpected error occurred");
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [fetchRequests]
  );

  const rejectRequest = useCallback(
    async (id: number): Promise<boolean> => {
      try {
        setActionLoading(true);
        const response = await rejectContributionRequest(id);
        if (response.success) {
          await fetchRequests();
          return true;
        }
        setError(response.message ?? "Failed to reject request");
        return false;
      } catch {
        setError("An unexpected error occurred");
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [fetchRequests]
  );

  return {
    requests,
    requestsLoading,
    requestsMeta,
    contributions,
    contributionsLoading,
    contributionsMeta,
    error,
    actionLoading,
    direction,
    setDirection,
    requestStatus,
    setRequestStatus,
    requestSearch,
    setRequestSearch,
    requestPage,
    setRequestPage,
    contributionStatus,
    setContributionStatus,
    contributionSearch,
    setContributionSearch,
    contributionPage,
    setContributionPage,
    acceptRequest,
    rejectRequest,
    refetchRequests: fetchRequests,
    refetchContributions: fetchContributions,
  };
}
